/* global google */
import React from 'react';
import PropTypes from 'prop-types';

import {withMap, withClusterer} from './utils';

class Marker extends React.Component {
  static displayName = 'Marker';

  static propTypes = {
    clusterer: PropTypes.object,
    map: PropTypes.object.isRequired,
    position: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }),
    icon: PropTypes.shape({
      anchor: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
      }),
      url: PropTypes.string,
    }),
    title: PropTypes.string,
    onClick: PropTypes.func,
    onDragEnd: PropTypes.func,
    animated: PropTypes.bool,
    info: PropTypes.string,
    radius: PropTypes.number,
    circleColor: PropTypes.string,
    zIndex: PropTypes.number,
    // Set this to true when deleting a map with a large number
    // of clustered markers.  If not set to true, unmounting the map
    // can take a really long time.
    // TODO: We need to figure out a better way to enforce declustering.
    ignoreDeclustering: PropTypes.bool,
  };

  componentDidMount() {
    this._marker = new google.maps.Marker({
      clickable: !!this.props.onClick || !!this.props.info,
      draggable: !!this.props.onDragEnd,
      icon: this._parseIcon(this.props.icon),
      map: this.props.map,
      position: this.props.position,
      title: this.props.title,
      optimized: !this.props.animated,
      zIndex: this.props.zIndex,
    });
    this._updateClickEvent();
    this._updateDragEvent();
    this._updateInfo();
    if (this.props.clusterer) this._addToClusterer();
    if (this.props.radius) {
      this._circle = new google.maps.Circle({
        map: this.props.map,
        radius: this.props.radius,
        fillColor: this.props.circleColor || '#AA0000',
      });
      this._circle.bindTo('center', this._marker, 'position');
    }
  }

  componentDidUpdate(prevProps) {
    this._updateOptions();
    if (this.props.onClick !== prevProps.onClick) this._updateClickEvent();
    if (this.props.onDragEnd !== prevProps.onDragEnd) this._updateDragEvent();
    if (this.props.clusterer !== prevProps.clusterer) this._addToClusterer();
    if (this.props.radius !== prevProps.radius ||
      this.props.circleColor !== prevProps.circleColor) this._updateRadius();
  }

  componentWillUnmount() {
    if (this.props.clusterer && !this.props.ignoreDeclustering) {
      this.props.clusterer.removeMarker(this._marker);
    }
    this._marker.setMap(null);
  }

  _parseIcon = (icon) => {
    if (!icon) return null;
    if (typeof(icon) === 'string') return icon;
    return {
      anchor: icon.anchor ? icon.anchor : {x: 0, y: 0},
      url: icon.url,
      scaledSize: icon.size,
    };
  };

  _addToClusterer = () => {
    if (this.props.clusterer) {
      this.props.clusterer.addMarker(this._marker);
    }
  };

  _updateOptions = () => {
    this._marker.setOptions({
      draggable: !!this.props.onDragEnd,
      icon: this._parseIcon(this.props.icon),
      position: this.props.position,
      optimized: !this.props.animated,
      zIndex: this.props.zIndex,
    });
  };

  _updateClickEvent = () => {
    google.maps.event.clearListeners(this._marker, 'click');
    if (typeof this.props.onClick === 'function') {
      google.maps.event.addListener(this._marker, 'click', () => this.props.onClick());
    }
    this._marker.setOptions({clickable: !!this.props.onClick || !!this.props.info});
  };

  _updateDragEvent = () => {
    google.maps.event.clearListeners(this._marker, 'dragend');
    if (typeof this.props.onDragEnd === 'function') {
      google.maps.event.addListener(this._marker, 'dragend', (event) => {
        this.props.onDragEnd({
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        });
      });
    }
  };

  _updateInfo = () => {
    if (this.props.info) {
      this._infoWindow = new google.maps.InfoWindow({
        content: `<div>${this.props.info}</div>`,
      });
      this._marker.addListener('click', () => {
        this._infoWindow.open(this.map, this._marker);
      });
    }
  };

  _updateRadius = () => {
    if (this._circle) {
      this._circle.setOptions({
        radius: this.props.radius,
        fillColor: this.props.circleColor || '#AA0000',
      });
    }
  };

  render() {
    return null;
  }
}

export default withMap(withClusterer(Marker));



// WEBPACK FOOTER //
// ./src/common/components/BetterMap/Marker.js